import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, LogIn, User, Crown, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { headerQuickLinks } from './navConfig';

// Responsive top header.
// Mobile (< lg): centered logo + hamburger (left) + alerts bell (right).
// Desktop (>= lg): quick links + user avatar/login on the right (logo lives in sidebar).
const Header = ({ onOpenDrawer, unreadCount = 0 }) => {
  const { user, isPro } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <header
      className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-[#E2E8F0] z-30 safe-area-top"
      data-testid="app-header"
    >
      {/* === MOBILE HEADER (< lg) === */}
      <div className="flex items-center justify-between px-3 py-2.5 lg:hidden">
        <button
          type="button"
          onClick={onOpenDrawer}
          className="p-2 -ml-2 rounded-md text-[#0F1C2E] hover:bg-[#F4F6F8] transition-colors"
          data-testid="mobile-hamburger-btn"
          aria-label="Abrir menu"
        >
          <Menu className="w-6 h-6" />
        </button>

        <Link to="/" className="flex items-center gap-2" data-testid="header-logo-mobile">
          <img src="/logo.png" alt="Tô de Olho" className="h-7 w-7 rounded-md" />
          <span className="text-sm font-black text-[#006030]">Tô de Olho</span>
        </Link>

        <Link
          to="/alertas"
          className="relative p-2 -mr-2 rounded-md text-[#0F1C2E] hover:bg-[#F4F6F8] transition-colors"
          data-testid="mobile-header-alerts"
          aria-label={`Alertas${unreadCount ? ` (${unreadCount} não lidos)` : ''}`}
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-[#C62828] text-white text-[9px] font-bold px-1"
              data-testid="header-alerts-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Link>
      </div>

      {/* === DESKTOP HEADER (>= lg) === */}
      <div className="hidden lg:flex items-center justify-end gap-1 px-6 py-2.5">
        <nav className="flex items-center gap-1 mr-3" aria-label="Atalhos">
          {headerQuickLinks.map((link) => {
            const active = location.pathname.startsWith(link.path);
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={link.testId}
                className={cn(
                  'px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors',
                  active
                    ? 'text-[#006030] bg-[#E8F5E9]'
                    : 'text-[#475569] hover:text-[#006030] hover:bg-[#F4F6F8]',
                )}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <Link
          to="/alertas"
          className="relative p-2 rounded-md text-[#0F1C2E] hover:bg-[#F4F6F8] transition-colors"
          data-testid="header-alerts-btn"
          aria-label="Alertas"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-[#C62828] text-white text-[9px] font-bold px-1">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Link>

        {user ? (
          <button
            onClick={() => navigate('/perfil')}
            className="ml-1 flex items-center gap-2 px-2 py-1 rounded-full hover:bg-[#F4F6F8] transition-colors"
            data-testid="header-avatar-btn"
          >
            <div className="w-8 h-8 rounded-full bg-[#006030] text-white flex items-center justify-center text-xs font-bold">
              {(user.nome || user.email || '?').slice(0, 1).toUpperCase()}
            </div>
            {isPro && <Crown className="w-3.5 h-3.5 text-[#F0D000]" />}
          </button>
        ) : (
          <button
            onClick={() => navigate('/auth')}
            className="ml-2 flex items-center gap-2 px-4 py-1.5 bg-[#006030] hover:bg-[#004020] text-white text-[13px] font-semibold rounded-md transition-colors"
            data-testid="header-login-btn"
          >
            <LogIn className="w-3.5 h-3.5" /> Entrar
          </button>
        )}
      </div>
    </header>
  );
};

export default Header;
